"use client"

import Link from "next/link"
import { useState } from "react"
import { ChevronDown } from "lucide-react"

import { ICONS, type IconName } from "./icon-map"

/**
 * Collapsible nav-section client component used by the custom Payload
 * sidebar (../index.tsx). Each section is open by default; the chevron
 * lets editors fold the less-used groups.
 *
 * Icons travel as STRING NAMES (`IconName`) from the server parent so
 * RSC serialization doesn't choke on function references — see the
 * comment in ./index.tsx for the underlying constraint. The lookup
 * happens locally via the `ICONS` map in ./icon-map.
 */

type NavItem = {
  label: string
  href: string
  icon: IconName
  matchPrefix?: boolean
}

export function NavSection({
  title,
  items,
  currentPath,
}: {
  title: string
  items: NavItem[]
  currentPath: string
}) {
  const [open, setOpen] = useState(true)

  const isActive = (it: NavItem) => {
    if (!currentPath) return false
    if (it.matchPrefix) return currentPath.startsWith(it.href)
    return currentPath === it.href
  }

  return (
    <div className="adis-nav__section">
      <button
        type="button"
        className="adis-nav__section-title"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={`nav-section-${title}`}
      >
        <span>{title}</span>
        <ChevronDown
          size={14}
          className={
            "adis-nav__section-chevron" +
            (open ? " adis-nav__section-chevron--open" : "")
          }
          aria-hidden
        />
      </button>

      <ul
        id={`nav-section-${title}`}
        className={
          "adis-nav__list" + (open ? " adis-nav__list--open" : " adis-nav__list--collapsed")
        }
      >
        {items.map((it) => {
          const Icon = ICONS[it.icon]
          const active = isActive(it)
          return (
            <li key={it.href}>
              <Link
                href={it.href}
                className={
                  "adis-nav__link" + (active ? " adis-nav__link--active" : "")
                }
                aria-current={active ? "page" : undefined}
              >
                <span className="adis-nav__link-icon" aria-hidden>
                  {Icon ? <Icon size={16} strokeWidth={1.75} /> : null}
                </span>
                <span className="adis-nav__link-label">{it.label}</span>
              </Link>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
